import React, { useState, useEffect, useRef } from "react";
import { notasFiscais, clientes, produtos } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { StatusBadge } from "../shared/StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Plus, Search, Eye, Trash2, Upload, Loader2, Edit, Save, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatCurrency, formatDate, formatPercent, cn } from "../../lib/utils";

export function NotasFiscaisPage() {
  const [data, setData] = useState([]);
  const [clientesList, setClientesList] = useState([]);
  const [produtosList, setProdutosList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [editingCustos, setEditingCustos] = useState(false);
  const [custosEdit, setCustosEdit] = useState([]);
  const [savingCustos, setSavingCustos] = useState(false);
  const [editingCustoTotal, setEditingCustoTotal] = useState(false);
  const [custoTotalEdit, setCustoTotalEdit] = useState(0);
  // Estado para edição inline de recebimentos
  const [editingRecebimento, setEditingRecebimento] = useState(null);
  const [recebimentoValue, setRecebimentoValue] = useState(0);
  const [recebimentoDisplay, setRecebimentoDisplay] = useState("");
  const fileInputRef = useRef(null);
  const excelInputRef = useRef(null);
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    numero_nf: "",
    serie: "1",
    numero_af: "",
    cliente_id: "",
    cliente_nome: "",
    data_emissao: new Date().toISOString().split("T")[0],
    valor_bruto: 0,
    valor_impostos: 0,
    valor_recebido: 0,
    observacoes: "",
    itens: [],
  });

  const [newItem, setNewItem] = useState({
    produto_id: "",
    produto_descricao: "",
    quantidade: 1,
    valor_unitario: 0,
    custo_unitario_aquisicao: 0,
  });

  const fetchData = async () => {
    try {
      const [nfsRes, clientesRes, produtosRes] = await Promise.all([
        notasFiscais.list(),
        clientes.list(),
        produtos.list(),
      ]);
      setData(nfsRes.data);
      setClientesList(clientesRes.data);
      setProdutosList(produtosRes.data);
    } catch (error) {
      toast.error("Erro ao carregar notas fiscais");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.itens.length === 0) {
      toast.error("Adicione pelo menos um item à nota fiscal");
      return;
    }
    try {
      await notasFiscais.create(formData);
      toast.success("Nota fiscal criada!");
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar NF");
    }
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clientesList.find((c) => c.id === clienteId);
    setFormData({
      ...formData,
      cliente_id: clienteId,
      cliente_nome: cliente?.razao_social || "",
    });
  };

  const handleProdutoChange = (produtoId) => {
    const produto = produtosList.find((p) => p.id === produtoId);
    setNewItem({
      ...newItem,
      produto_id: produtoId,
      produto_descricao: produto?.descricao || "",
      valor_unitario: produto?.preco_venda_padrao || 0,
      custo_unitario_aquisicao: produto?.custo_unitario_compra || 0,
    });
  };

  const addItem = () => {
    if (!newItem.produto_id || newItem.quantidade <= 0) {
      toast.error("Selecione um produto e quantidade válida");
      return;
    }
    const itemTotal = newItem.quantidade * newItem.valor_unitario;
    setFormData({
      ...formData,
      itens: [...formData.itens, newItem],
      valor_bruto: formData.valor_bruto + itemTotal,
    });
    setNewItem({
      produto_id: "",
      produto_descricao: "",
      quantidade: 1,
      valor_unitario: 0,
      custo_unitario: 0,
    });
  };

  const removeItem = (index) => {
    const item = formData.itens[index];
    const itemTotal = item.quantidade * item.valor_unitario;
    setFormData({
      ...formData,
      itens: formData.itens.filter((_, i) => i !== index),
      valor_bruto: formData.valor_bruto - itemTotal,
    });
  };

  const resetForm = () => {
    setFormData({
      numero_nf: "",
      serie: "1",
      numero_af: "",
      cliente_id: "",
      cliente_nome: "",
      data_emissao: new Date().toISOString().split("T")[0],
      valor_bruto: 0,
      valor_impostos: 0,
      valor_recebido: 0,
      observacoes: "",
      itens: [],
    });
    setNewItem({
      produto_id: "",
      produto_descricao: "",
      quantidade: 1,
      valor_unitario: 0,
      custo_unitario_aquisicao: 0,
    });
  };

  const handleStartEditCustos = () => {
    if (viewItem?.itens) {
      setCustosEdit(viewItem.itens.map((item, index) => ({
        index: index,  // Usar índice ao invés de produto_id
        produto_descricao: item.produto_descricao,
        custo_unitario_aquisicao: item.custo_unitario_aquisicao || 0,
        valor_unitario: item.valor_unitario,
        quantidade: item.quantidade,
      })));
      setEditingCustos(true);
    }
  };

  const handleCustoChange = (index, value) => {
    const updated = [...custosEdit];
    updated[index].custo_unitario_aquisicao = parseFloat(value) || 0;
    setCustosEdit(updated);
  };

  const handleSaveCustos = async () => {
    if (!viewItem?.id) return;
    
    setSavingCustos(true);
    try {
      const response = await notasFiscais.atualizarCustos(viewItem.id, {
        itens: custosEdit.map(item => ({
          index: item.index,  // Enviar índice
          custo_unitario_aquisicao: item.custo_unitario_aquisicao,
        })),
      });
      
      // Update viewItem with new data
      setViewItem(response.data);
      setEditingCustos(false);
      toast.success("Custos de aquisição atualizados! Margens recalculadas.");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar custos de aquisição");
    } finally {
      setSavingCustos(false);
    }
  };

  const handleCancelEditCustos = () => {
    setEditingCustos(false);
    setCustosEdit([]);
  };

  const handleSaveCustoTotal = async () => {
    if (!viewItem?.id) return;
    setSavingCustos(true);
    try {
      const response = await notasFiscais.atualizarCustoTotal(viewItem.id, {
        custo_total_aquisicao: custoTotalEdit,
      });
      setViewItem(response.data);
      setEditingCustoTotal(false);
      toast.success("Custo total de aquisição atualizado!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar custo total de aquisição");
    } finally {
      setSavingCustos(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir esta nota fiscal?")) return;
    try {
      await notasFiscais.delete(id);
      toast.success("Nota fiscal excluída!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir nota fiscal");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de arquivo não suportado. Use PDF, PNG, JPG ou WEBP.");
      return;
    }

    setUploading(true);
    try {
      const response = await notasFiscais.upload(file);
      const extractedData = response.data;

      // Find cliente by name or CNPJ
      let clienteId = "";
      let clienteNome = extractedData.cliente_nome || "";
      
      if (extractedData.cliente_cnpj) {
        const cliente = clientesList.find(c => 
          c.cnpj?.replace(/\D/g, "") === extractedData.cliente_cnpj?.replace(/\D/g, "")
        );
        if (cliente) {
          clienteId = cliente.id;
          clienteNome = cliente.razao_social;
        }
      } else if (extractedData.cliente_nome) {
        const cliente = clientesList.find(c => 
          c.razao_social?.toLowerCase().includes(extractedData.cliente_nome.toLowerCase()) ||
          c.nome_fantasia?.toLowerCase().includes(extractedData.cliente_nome.toLowerCase())
        );
        if (cliente) {
          clienteId = cliente.id;
          clienteNome = cliente.razao_social;
        }
      }

      // Process items
      const processedItems = (extractedData.itens || []).map(item => ({
        produto_id: "",
        produto_descricao: item.descricao || "",
        quantidade: parseFloat(item.quantidade) || 1,
        valor_unitario: parseFloat(item.valor_unitario) || 0,
        custo_unitario_aquisicao: 0,
      }));

      // Update form with extracted data
      setFormData({
        numero_nf: extractedData.numero_nf || "",
        serie: extractedData.serie || "1",
        cliente_id: clienteId,
        cliente_nome: clienteNome,
        data_emissao: extractedData.data_emissao || new Date().toISOString().split("T")[0],
        valor_bruto: extractedData.valor_bruto || 0,
        valor_impostos: extractedData.valor_impostos || 0,
        observacoes: extractedData.observacoes || "",
        itens: processedItems,
      });

      toast.success("Dados extraídos com sucesso! Verifique e complete as informações.");
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Erro ao processar arquivo");
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Upload de Excel com NFs
  const handleExcelUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error("Apenas arquivos .xlsx ou .xls são suportados.");
      return;
    }

    setUploadingExcel(true);
    try {
      const response = await notasFiscais.importarExcel(file);
      const result = response.data;
      toast.success(result.message);
      if (result.erros?.length > 0) {
        result.erros.forEach((err) => toast.warning(err));
      }
      fetchData();
    } catch (error) {
      console.error("Excel upload error:", error);
      toast.error(error.response?.data?.detail || "Erro ao importar Excel");
    } finally {
      setUploadingExcel(false);
      if (excelInputRef.current) {
        excelInputRef.current.value = "";
      }
    }
  };

  // Função para salvar valor recebido inline
  const handleSaveRecebimento = async (nfId) => {
    try {
      await notasFiscais.atualizarRecebimento(nfId, {
        valor_recebido: recebimentoValue
      });
      toast.success("Valor recebido atualizado!");
      setEditingRecebimento(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar valor recebido");
    }
  };

  const filteredData = data.filter(
    (item) =>
      item.numero_nf?.toLowerCase().includes(search.toLowerCase()) ||
      item.cliente_nome?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { key: "numero_nf", header: "Número", width: "100px" },
    { key: "serie", header: "Série", width: "60px" },
    { key: "cliente_nome", header: "Cliente" },
    { key: "data_emissao", header: "Emissão", render: (v) => formatDate(v) },
    { key: "valor_bruto", header: "Valor Bruto", render: (v) => formatCurrency(v) },
    { key: "valor_liquido", header: "Valor Líquido", render: (v) => formatCurrency(v) },
    { 
      key: "valor_recebido", 
      header: "Valor Recebido", 
      render: (v, row) => (
        editingRecebimento === row.id ? (
          <div className="flex items-center gap-1">
            <Input
              type="text"
              inputMode="decimal"
              className="w-28 h-8 text-sm"
              value={recebimentoDisplay}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d.,]/g, '');
                setRecebimentoDisplay(raw);
                const parsed = parseFloat(raw.replace(',', '.'));
                setRecebimentoValue(isNaN(parsed) ? 0 : parsed);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveRecebimento(row.id);
                if (e.key === 'Escape') setEditingRecebimento(null);
              }}
              autoFocus
              onFocus={(e) => e.target.select()}
              data-testid="input-valor-recebido"
            />
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleSaveRecebimento(row.id)} data-testid="btn-save-recebimento">
              <Save className="h-3 w-3 text-green-600" />
            </Button>
          </div>
        ) : (
          <div 
            className={cn(
              "cursor-pointer hover:bg-green-50 px-2 py-1 rounded",
              isAdmin() && "hover:ring-1 hover:ring-green-300"
            )}
            onClick={() => {
              if (isAdmin()) {
                setEditingRecebimento(row.id);
                const currentVal = v || 0;
                setRecebimentoValue(currentVal);
                setRecebimentoDisplay(currentVal > 0 ? String(currentVal) : "");
              }
            }}
            title={isAdmin() ? "Clique para editar" : ""}
            data-testid={`valor-recebido-cell-${row.id}`}
          >
            <span className="text-green-600 font-medium">{formatCurrency(v || 0)}</span>
          </div>
        )
      )
    },
    { 
      key: "saldo_receber", 
      header: "A Receber", 
      render: (_, row) => {
        const saldo = (row.valor_bruto || 0) - (row.valor_recebido || 0);
        return (
          <span className={cn(
            "font-medium",
            saldo > 0 ? "text-orange-600" : "text-green-600"
          )} data-testid={`saldo-receber-cell-${row.id}`}>
            {formatCurrency(saldo)}
          </span>
        );
      }
    },
    {
      key: "custo_total_aquisicao",
      header: "Vlr Líq. Aquisição",
      render: (v) => (
        <span className="text-blue-600 font-medium" data-testid="vla-cell">
          {formatCurrency(v || 0)}
        </span>
      )
    },
    { 
      key: "margem_percentual", 
      header: "Margem", 
      render: (v, row) => {
        const vla = row.custo_total_aquisicao || 0;
        const valorRecebido = row.valor_recebido || 0;
        const aReceber = (row.valor_bruto || 0) - valorRecebido;
        const numerador = valorRecebido + aReceber;
        const margem = vla > 0 ? ((numerador / vla) - 1) * 100 : 0;
        return (
          <span className={cn(
            "font-medium",
            margem > 0 ? "text-green-600" : margem < 0 ? "text-red-600" : ""
          )}>
            {vla > 0 ? `${margem.toFixed(1)}%` : "0,0%"}
          </span>
        );
      }
    },
    { key: "status_nf", header: "Status", render: (v) => <StatusBadge status={v} /> },
    {
      key: "actions",
      header: "",
      width: "100px",
      render: (_, row) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => setViewItem(row)} data-testid={`view-nf-${row.id}`}>
            <Eye className="h-4 w-4" />
          </Button>
          {isAdmin() && (
            <Button variant="ghost" size="icon" onClick={() => handleDelete(row.id)} data-testid={`delete-nf-${row.id}`}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="notas-fiscais-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Notas Fiscais</h1>
          <p className="text-muted-foreground">Gerencie as notas fiscais emitidas</p>
        </div>
        {isAdmin() && (
          <div className="flex gap-2">
            {/* Hidden file input for PDF/image */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              className="hidden"
              data-testid="nf-file-input"
            />
            {/* Hidden file input for Excel */}
            <input
              type="file"
              ref={excelInputRef}
              onChange={handleExcelUpload}
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="nf-excel-input"
            />
            <Button
              variant="outline"
              onClick={() => excelInputRef.current?.click()}
              disabled={uploadingExcel}
              data-testid="import-excel-btn"
            >
              {uploadingExcel ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Importar Excel
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-nf-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Importar NF
                </>
              )}
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-nf-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova NF
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nova Nota Fiscal</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Número da NF *</Label>
                      <Input
                        value={formData.numero_nf}
                        onChange={(e) => setFormData({ ...formData, numero_nf: e.target.value })}
                        required
                        data-testid="nf-numero"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Série</Label>
                      <Input
                      value={formData.serie}
                      onChange={(e) => setFormData({ ...formData, serie: e.target.value })}
                      data-testid="nf-serie"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Número da AF</Label>
                    <Input
                      value={formData.numero_af}
                      onChange={(e) => setFormData({ ...formData, numero_af: e.target.value })}
                      placeholder="Autorização de Fornecimento"
                      data-testid="nf-numero-af"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Emissão *</Label>
                    <Input
                      type="date"
                      value={formData.data_emissao}
                      onChange={(e) => setFormData({ ...formData, data_emissao: e.target.value })}
                      required
                      data-testid="nf-data"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <Select value={formData.cliente_id} onValueChange={handleClienteChange}>
                      <SelectTrigger data-testid="nf-cliente">
                        <SelectValue placeholder="Selecione o cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientesList.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Impostos</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.valor_impostos}
                      onChange={(e) => setFormData({ ...formData, valor_impostos: parseFloat(e.target.value) || 0 })}
                      data-testid="nf-impostos"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Valor Recebido</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.valor_recebido}
                      onChange={(e) => setFormData({ ...formData, valor_recebido: parseFloat(e.target.value) || 0 })}
                      placeholder="R$ 0,00"
                      data-testid="nf-valor-recebido"
                    />
                  </div>
                </div>

                {/* Items section */}
                <div className="border rounded-sm p-4 space-y-4">
                  <h3 className="font-semibold">Itens da Nota</h3>
                  <div className="grid grid-cols-5 gap-2">
                    <div className="col-span-2">
                      <Select value={newItem.produto_id} onValueChange={handleProdutoChange}>
                        <SelectTrigger data-testid="nf-item-produto">
                          <SelectValue placeholder="Produto" />
                        </SelectTrigger>
                        <SelectContent>
                          {produtosList.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.descricao}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      type="number"
                      placeholder="Qtd"
                      value={newItem.quantidade}
                      onChange={(e) => setNewItem({ ...newItem, quantidade: parseInt(e.target.value) || 0 })}
                      data-testid="nf-item-qtd"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Valor"
                      value={newItem.valor_unitario}
                      onChange={(e) => setNewItem({ ...newItem, valor_unitario: parseFloat(e.target.value) || 0 })}
                      data-testid="nf-item-valor"
                    />
                    <Button type="button" onClick={addItem} data-testid="nf-add-item">Adicionar</Button>
                  </div>

                  {formData.itens.length > 0 && (
                    <div className="border rounded-sm overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2">Produto</th>
                            <th className="text-right p-2">Qtd</th>
                            <th className="text-right p-2">Valor Unit.</th>
                            <th className="text-right p-2">Total</th>
                            <th className="p-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.itens.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="p-2">{item.produto_descricao}</td>
                              <td className="p-2 text-right">{item.quantidade}</td>
                              <td className="p-2 text-right">{formatCurrency(item.valor_unitario)}</td>
                              <td className="p-2 text-right">{formatCurrency(item.quantidade * item.valor_unitario)}</td>
                              <td className="p-2">
                                <Button variant="ghost" size="icon" onClick={() => removeItem(index)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-lg font-semibold">
                    Total: {formatCurrency(formData.valor_bruto)}
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" data-testid="save-nf-btn">Criar NF</Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por número ou cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-nfs"
          />
        </div>
      </div>

      <DataTable columns={columns} data={filteredData} loading={loading} emptyMessage="Nenhuma nota fiscal encontrada" />

      {/* View Dialog */}
      <Dialog open={!!viewItem} onOpenChange={(open) => { 
        if (!open) {
          setViewItem(null);
          setEditingCustos(false);
          setCustosEdit([]);
          setEditingCustoTotal(false);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Nota Fiscal #{viewItem?.numero_nf}</span>
              {isAdmin() && !editingCustos && !editingCustoTotal && (
                viewItem?.itens?.length > 0 ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleStartEditCustos}
                    data-testid="edit-custos-btn"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Editar Custos
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setCustoTotalEdit(viewItem?.custo_total_aquisicao || 0);
                      setEditingCustoTotal(true);
                    }}
                    data-testid="edit-custo-total-btn"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Editar Custos
                  </Button>
                )
              )}
            </DialogTitle>
          </DialogHeader>
          {viewItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Cliente:</span> {viewItem.cliente_nome}</div>
                <div><span className="text-muted-foreground">Data:</span> {formatDate(viewItem.data_emissao)}</div>
                {viewItem.numero_af && (
                  <div><span className="text-muted-foreground">Nº AF:</span> <span className="font-medium">{viewItem.numero_af}</span></div>
                )}
                <div><span className="text-muted-foreground">Valor Bruto:</span> {formatCurrency(viewItem.valor_bruto)}</div>
                <div><span className="text-muted-foreground">Impostos:</span> {formatCurrency(viewItem.valor_impostos)}</div>
                <div><span className="text-muted-foreground">Valor Líquido:</span> <span className="font-semibold text-green-600">{formatCurrency(viewItem.valor_liquido)}</span></div>
                <div><span className="text-muted-foreground">Custo Total Aquisição:</span> <span className="font-semibold text-orange-600">{formatCurrency(viewItem.custo_total_aquisicao || 0)}</span></div>
              </div>
              
              {/* Margem Summary */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">Margem Total (R$):</span>
                    <p className={cn(
                      "text-xl font-bold",
                      viewItem.margem_total >= 0 ? "text-green-600" : "text-red-600"
                    )}>
                      {formatCurrency(viewItem.margem_total || 0)}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Margem (%):</span>
                    <p className={cn(
                      "text-xl font-bold",
                      viewItem.margem_percentual >= 0 ? "text-green-600" : "text-red-600"
                    )}>
                      {viewItem.custo_total_aquisicao > 0 ? formatPercent(viewItem.margem_percentual) : "Informe os custos"}
                    </p>
                  </div>
                </div>
              </div>
              
              {viewItem.itens?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Itens da Nota Fiscal</h4>
                  
                  {editingCustos ? (
                    /* Editing Mode */
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Informe o custo unitário de aquisição para cada produto. A margem será calculada automaticamente.
                      </p>
                      <div className="border rounded-sm overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-muted">
                            <tr>
                              <th className="text-left p-2">Produto</th>
                              <th className="text-right p-2">Qtd</th>
                              <th className="text-right p-2">Valor Venda</th>
                              <th className="text-right p-2 bg-orange-100">Custo Aquisição</th>
                            </tr>
                          </thead>
                          <tbody>
                            {custosEdit.map((item, index) => (
                              <tr key={index} className="border-t">
                                <td className="p-2">{item.produto_descricao}</td>
                                <td className="p-2 text-right">{item.quantidade}</td>
                                <td className="p-2 text-right">{formatCurrency(item.valor_unitario)}</td>
                                <td className="p-2 bg-orange-50">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={item.custo_unitario_aquisicao}
                                    onChange={(e) => handleCustoChange(index, e.target.value)}
                                    className="w-28 text-right ml-auto"
                                    data-testid={`custo-input-${index}`}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={handleCancelEditCustos} disabled={savingCustos}>
                          Cancelar
                        </Button>
                        <Button onClick={handleSaveCustos} disabled={savingCustos} data-testid="save-custos-btn">
                          {savingCustos ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Salvando...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Salvar Custos
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="border rounded-sm overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2">Produto</th>
                            <th className="text-right p-2">Qtd</th>
                            <th className="text-right p-2">Valor Venda</th>
                            <th className="text-right p-2">Custo Aquis.</th>
                            <th className="text-right p-2">Margem Bruta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewItem.itens.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="p-2">{item.produto_descricao}</td>
                              <td className="p-2 text-right">{item.quantidade}</td>
                              <td className="p-2 text-right">{formatCurrency(item.valor_unitario)}</td>
                              <td className="p-2 text-right text-orange-600">
                                {item.custo_unitario_aquisicao > 0 ? formatCurrency(item.custo_unitario_aquisicao) : "-"}
                              </td>
                              <td className={cn(
                                "p-2 text-right font-semibold",
                                item.margem_bruta_percentual > 0 ? "text-green-600" : 
                                item.margem_bruta_percentual < 0 ? "text-red-600" : "text-muted-foreground"
                              )}>
                                {item.custo_unitario_aquisicao > 0 ? formatPercent(item.margem_bruta_percentual) : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-100 font-semibold">
                          <tr>
                            <td className="p-2" colSpan={2}>Total NF (sobre Valor Líquido)</td>
                            <td className="p-2 text-right text-blue-600">{formatCurrency(viewItem.valor_liquido)}</td>
                            <td className="p-2 text-right text-orange-600">{formatCurrency(viewItem.custo_total_aquisicao || 0)}</td>
                            <td className={cn(
                              "p-2 text-right",
                              viewItem.margem_percentual > 0 ? "text-green-600" : 
                              viewItem.margem_percentual < 0 ? "text-red-600" : ""
                            )}>
                              {viewItem.custo_total_aquisicao > 0 ? formatPercent(viewItem.margem_percentual) : "-"}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Edição de Custo Total para NFs sem itens */}
              {(!viewItem.itens || viewItem.itens.length === 0) && editingCustoTotal && (
                <div className="space-y-4 border rounded-lg p-4 bg-orange-50">
                  <h4 className="font-semibold">Custo Total de Aquisição</h4>
                  <p className="text-sm text-muted-foreground">
                    Informe o custo total de aquisição desta NF. A margem será recalculada automaticamente.
                  </p>
                  <div className="flex items-center gap-3">
                    <Label className="text-sm font-medium whitespace-nowrap">Custo Total (R$):</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={custoTotalEdit}
                      onChange={(e) => setCustoTotalEdit(parseFloat(e.target.value) || 0)}
                      className="w-48"
                      autoFocus
                      data-testid="custo-total-input"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setEditingCustoTotal(false)} disabled={savingCustos}>
                      Cancelar
                    </Button>
                    <Button onClick={handleSaveCustoTotal} disabled={savingCustos} data-testid="save-custo-total-btn">
                      {savingCustos ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Salvar Custo
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
